import React from 'react';
import { useMemo } from 'react';
// Constant for representing dots in pagination
export const DOTS = '...';
// Function to generate a range of numbers
const range = (start, end) => {
    let length = end - start + 1;
    return Array.from({ length }, (_, idx) => idx + start);
};
/**
 * Custom hook for pagination logic.
 * @param {Object} param0 - The object containing pagination configurations.
 * @param {number} [param0.siblingCount=1] - The number of sibling page links to display.
 * @param {number} param0.currentPage - The current active page.
 * @param {number} param0.totalPageCount - The total number of pages.
 * @returns {Array} An array representing the pagination range.
 */
export const useCustomPagination = ({
    siblingCount = 1, // Default sibling count is 1
    currentPage, // Current active page
    totalPageCount, // Total number of pages
}) => {
    const paginationRange = useMemo(() => {
        // Calculate the total number of page numbers to be displayed
        const totalPageNumbers = siblingCount + 5;
        // If totalPageNumbers is greater than or equal to totalPageCount, display all pages
        if (totalPageNumbers >= totalPageCount) {
            return range(1, totalPageCount);
        }
        // Calculate left and right sibling indexes
        const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
        const rightSiblingIndex = Math.min(
            currentPage + siblingCount,
            totalPageCount
        );
        // Determine whether to show dots on the left and right sides
        const shouldShowLeftDots = leftSiblingIndex > 2;
        const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;
        // Define first and last page indexes
        const firstPageIndex = 1;
        const lastPageIndex = totalPageCount;
        // Return pagination range based on conditions
        if (!shouldShowLeftDots && shouldShowRightDots) {
            // If dots should not be shown on the left side but should be shown on the right side,
            // calculate the number of items to be displayed on the left side, including the dots.
            let leftItemCount = 3 + 2 * siblingCount;
            let leftRange = range(1, leftItemCount);
            return [...leftRange, DOTS, totalPageCount];
        }
        if (shouldShowLeftDots && !shouldShowRightDots) {
            // If dots should be shown on the left side but should not be shown on the right side,
            // calculate the number of items to be displayed on the right side, including the dots.
            let rightItemCount = 3 + 2 * siblingCount;
            let rightRange = range(
                totalPageCount - rightItemCount + 1,
                totalPageCount
            );
            return [firstPageIndex, DOTS, ...rightRange];
        }
        if (shouldShowLeftDots && shouldShowRightDots) {
            // If dots should be shown on both the left and right sides,
            // generate a range of page numbers from leftSiblingIndex to rightSiblingIndex.
            let middleRange = range(leftSiblingIndex, rightSiblingIndex);
            return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
        }
    }, [totalPageCount, siblingCount, currentPage]);
    return paginationRange; // Return the calculated pagination range
};
